// Bet Cracker — interactive panel with cursor-tracking spotlight + 3-D tilt.
//
// Wrap any block of UI to make it react to cursor movement *while*
// hovering: a soft radial spotlight follows the pointer, the panel tilts
// subtly in 3-D, and a thin neon border glow brightens. Designed to give
// MatchPage panels constant motion without being distracting.

import { useEffect, useRef } from "react";
import { tilt3D } from "@/lib/motion";

const REDUCED = typeof window !== "undefined"
  && window.matchMedia?.("(pointer: coarse)")?.matches; // skip on touch

export default function HoverPanel({
  children,
  color = "rgba(34,197,94,0.18)",   // accent for the spotlight ring
  borderGlow = "rgba(34,197,94,0.45)",
  tilt = true,
  tiltMax = 6,                       // visible tilt — was 3°, too subtle on tall panels
  spotlight = true,
  spotlightSize = 320,               // px diameter of the spot
  className = "",
  style = {},
  testid,
}) {
  const ref = useRef(null);
  const overlayRef = useRef(null);

  // 3-D tilt — reuses the shared lib helper
  useEffect(() => {
    if (!ref.current || !tilt || REDUCED) return;
    return tilt3D(ref.current, { max: tiltMax, perspective: 1300, scaleHover: 1.012 });
  }, [tilt, tiltMax]);

  // Spotlight follow — on every mousemove, push the X/Y into CSS vars on
  // the overlay so the radial gradient redraws instantly. We also fade
  // the overlay in/out on enter/leave for a subtle "torch in the dark"
  // effect. Pure CSS update — extremely cheap (no React re-renders).
  useEffect(() => {
    const el = ref.current;
    const ov = overlayRef.current;
    if (!el || !ov || !spotlight || REDUCED) return;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        ov.style.setProperty("--mx", `${e.clientX - r.left}px`);
        ov.style.setProperty("--my", `${e.clientY - r.top}px`);
      });
    };
    const onEnter = () => { ov.style.opacity = "1"; };
    const onLeave = () => { ov.style.opacity = "0"; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [spotlight]);

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={className}
      style={{
        position: "relative",
        transformStyle: "preserve-3d",
        willChange: "transform",
        transition: "border-color 220ms ease",
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.zIndex = 5; }}
      onMouseLeave={(e) => { e.currentTarget.style.zIndex = ""; }}
    >
      {/* Spotlight overlay — radial gradient redrawn from CSS vars on every
          mousemove (rAF-throttled). Pointer-events:none so it doesn't
          block clicks underneath. */}
      {spotlight && (
        <div
          ref={overlayRef}
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0,
            transition: "opacity 320ms ease",
            background: `radial-gradient(circle ${spotlightSize}px at var(--mx, -100px) var(--my, -100px), ${color} 0%, transparent 65%)`,
            mixBlendMode: "screen",
            zIndex: 1,
            boxShadow: `inset 0 0 0 1px ${borderGlow}, 0 0 24px -8px ${borderGlow}`,
          }}
        />
      )}
      {/* Real content sits above the spotlight overlay */}
      <div style={{ position: "relative", zIndex: 2 }}>
        {children}
      </div>
    </div>
  );
}
